import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Col, Row } from 'antd';

import useFilterAccess from 'components/Filters/hooks/useFilterAccess';

import applicationsService from 'features/applications/applicationsService';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import { selectAppLocale } from 'features/locale/localeSelectors';

import useErrorCatch from 'hooks/useErrorCatch';
import { useFilters } from 'hooks/useFilters';
import { useTranslatedCountriesOptions } from 'hooks/useTranslatedCountriesOptions';

import formatAndOrderData from 'utils/formatAndOrderData';
import formatOptionsWithLabel from 'utils/formatOptionsWithLabel';

import { ApplicationJobOptions } from 'types/applications';

import applicationStagesOrder from 'constants/applicationStagesOrder';
import { JOBSEEKERS_COUNTRIES } from 'constants/endpoints';
import { AppModules, Dictionaries, FilterFields } from 'constants/enums';
import languagesOrder from 'constants/languagesOrder';

import styles from './ApplicationsFilters.module.scss';
import FilterWrapper from './common/FilterWrapper';
import SelectCommon from './common/SelectCommon/SelectCommon';
import CountryStateCitySelect from './components/CountryStateCitySelect';
import SkillsetSelect from './components/SkillsetSelect';
import { SelectValue } from './filter.types';

const ApplicationsFilters = () => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const [applicationJobs, setApplicationJobs] = useState<ApplicationJobOptions>(
    {}
  );
  const [isApplicationJobsLoading, setIsApplicationJobsLoading] =
    useState(false);
  const { hasAccessToFilter } = useFilterAccess('applicationFilters');
  const { filters, updateFilter, isLoading } = useFilters(
    AppModules.Applications
  );

  const locale = useSelector(selectAppLocale);

  const {
    education,
    jobIds,
    jobseekerLocationsCountry,
    jobseekerLocationsState,
    jobseekerLocationsCity,
    languages,
    skillset,
    stage
  } = filters;

  const applicationStagesDict = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClient)
  );

  const educationOptions = useSelector(
    selectDictionary(Dictionaries.Education)
  );

  const applicationStagesOptions = formatOptionsWithLabel(
    formatAndOrderData(applicationStagesDict, applicationStagesOrder)
  );

  const { options: countries, isLoading: isCountriesLoading } =
    useTranslatedCountriesOptions({
      url: JOBSEEKERS_COUNTRIES,
      locale
    });

  const applicationJobsOptions = Object.entries(applicationJobs)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const languagesDict = useSelector(selectDictionary(Dictionaries.Language));

  const languagesOptions = formatAndOrderData(languagesDict, languagesOrder);

  const fetchApplicationJobs = async (): Promise<void> => {
    try {
      setIsApplicationJobsLoading(true);
      const result = await applicationsService.getApplicationJobsDictionary();
      setApplicationJobs(result);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.applicationJobs')
        }
      });
    } finally {
      setIsApplicationJobsLoading(false);
    }
  };

  const handleJobIdsChange = (value: SelectValue) => {
    updateFilter(FilterFields.JobIds, Array.isArray(value) ? value : []);
  };

  const handleStageChange = (value: SelectValue) => {
    updateFilter(FilterFields.Stage, Array.isArray(value) ? value : []);
  };

  const handleLanguagesChange = (value: SelectValue) => {
    updateFilter(FilterFields.Languages, Array.isArray(value) ? value : []);
  };

  const handleJobseekerLocationsCountryChange = (value?: string) => {
    const formattedValue = value === 'US' ? 'USA' : value;
    updateFilter(FilterFields.JobseekerLocationsCountry, formattedValue);
  };

  const handleJobseekerLocationsStateChange = (value?: string) => {
    updateFilter(FilterFields.JobseekerLocationsState, value);
  };

  const handleJobseekerLocationsCityChange = (value?: string) => {
    updateFilter(FilterFields.JobseekerLocationsCity, value);
  };

  const handleSkillsetChange = (value: SelectValue) => {
    updateFilter(FilterFields.Skillset, Array.isArray(value) ? value : []);
  };

  const handleEducationChange = (value: SelectValue) => {
    updateFilter(FilterFields.Education, Array.isArray(value) ? value : []);
  };

  useEffect(() => {
    fetchApplicationJobs();
  }, []);

  return (
    <FilterWrapper>
      <Row className={styles.row}>
        <Col span={24}>
          <div className={styles.sectionHeader}>
            {t('application.jobDetails')}
          </div>
        </Col>
        <Col span={24}>
          <div className={styles.divider} />
        </Col>
      </Row>

      <SelectCommon
        title={t('application.jobApplied')}
        value={jobIds}
        options={applicationJobsOptions}
        onChange={handleJobIdsChange}
        placeholder={t('application.selectJobApplied')}
        disabled={isApplicationJobsLoading || isLoading}
        showSearch
        hasAccessToFilter={hasAccessToFilter('jobIds')}
      />

      <SelectCommon
        title={t('application.applicationStatus')}
        value={stage}
        options={applicationStagesOptions}
        onChange={handleStageChange}
        placeholder={t('application.selectApplicationStatus')}
        disabled={isLoading}
        showSearch
        hasAccessToFilter={hasAccessToFilter('stage')}
      />

      <Row className={styles.row}>
        <Col span={24}>
          <div className={styles.sectionHeader}>
            {t('application.jobseekerInfo')}
          </div>
        </Col>
        <Col span={24}>
          <div className={styles.divider} />
        </Col>
      </Row>

      <CountryStateCitySelect
        countryTitle={t('common.country')}
        stateTitle={t('common.state')}
        cityTitle={t('common.city')}
        countryValue={
          jobseekerLocationsCountry === 'USA' ? 'US' : jobseekerLocationsCountry
        }
        stateValue={jobseekerLocationsState}
        cityValue={jobseekerLocationsCity}
        onChangeCountry={handleJobseekerLocationsCountryChange}
        onChangeState={handleJobseekerLocationsStateChange}
        onChangeCity={handleJobseekerLocationsCityChange}
        countryPlaceholder="application.countrySelect.countryPlaceholder"
        statePlaceholder="application.countrySelect.statePlaceholder"
        cityPlaceholder="application.countrySelect.cityPlaceholder"
        customCountries={countries}
        disabled={isCountriesLoading || isLoading}
        hasAccessToFilter={hasAccessToFilter}
      />

      <Row className={styles.row}>
        <Col span={24}>
          <div className={styles.sectionHeader}>{t('application.skills')}</div>
        </Col>
        <Col span={24}>
          <div className={styles.divider} />
        </Col>
      </Row>

      <SkillsetSelect
        value={skillset}
        onChange={handleSkillsetChange}
        hasAccessToFilter={hasAccessToFilter('skillset')}
        disabled={isLoading}
      />

      <SelectCommon
        title={t('application.languages')}
        value={languages}
        options={languagesOptions}
        onChange={handleLanguagesChange}
        placeholder={t('application.languagesPlaceholder')}
        disabled={isLoading}
        showSearch
        hasAccessToFilter={hasAccessToFilter('languages')}
      />

      <SelectCommon
        title={t('application.education')}
        value={education}
        options={educationOptions}
        onChange={handleEducationChange}
        placeholder={t('application.educationPlaceholder')}
        disabled={isLoading}
        showSearch
        hasAccessToFilter={hasAccessToFilter('education')}
      />
    </FilterWrapper>
  );
};

export default ApplicationsFilters;
